import { useMutation } from '@apollo/client';
import { useCallback, useRef } from 'react';

import {
  CreatePaymentMutation,
  PaymentCreateYigimTemplateGroupEnum
} from '@/__generated_graphql__/graphql';
import {
  AdTypeEnum,
  MediumEnum,
  PaidService,
  PaidTypeEnum,
  PaymentCreateInput,
  PaymentCreateStatusEnum,
  PaymentMethodEnum,
  PaymentResult
} from '@/__generated_graphql__/types';
import { EMPTY_BALANCE_CODE } from '@/components/AdvertisePayments/AdvertisePayments.constants';
import {
  canPayWithWallet,
  createFailedResult,
  isWalletMethod,
  mapTypenameToAdType,
  selectOptionPrice,
  selectWalletBalance
} from '@/components/AdvertisePayments/AdvertisePayments.helpers';
import { HandlePaymentArgs } from '@/components/AdvertisePayments/hooks/types';
import { useModal } from '@/components/AdvertisePayments/hooks/useModal';
import { usePaymentMessageListener } from '@/components/AdvertisePayments/hooks/usePaymentMessageListener';
import { usePaymentModalsManager } from '@/components/AdvertisePayments/hooks/usePaymentModalsManager';
import { PaymentTypeEnum } from '@/constants/common';
import { CREATE_PAYMENT } from '@/graphql/mutations/payments.mutations';
import { useBelow } from '@/hooks/useBelow';
import { CardAd } from '@/types';

import { ExtraContentModalProps } from '../components/ExtraContent/ExtraContent.types';
import { usePaymentWindow } from './usePaymentWindow';

export type UsePaymentHandlerArgs = {
  modals: ReturnType<typeof usePaymentModalsManager>;
  mainModal: ReturnType<typeof useModal>;
  adId?: string | null;
  ad?: CardAd;
  platform?: MediumEnum;
  onError?: () => void;
  onSuccess?: (
    method?: PaymentMethodEnum,
    isAddBalance?: boolean
  ) => void | Promise<void>;
  type?: PaymentTypeEnum;
  paidServices: PaidService[];
  paidType?: PaidTypeEnum | null;
  postpaidLimit?: number | null;
  isBillingProfilePending?: boolean;
};

export const usePaymentHandler = ({
  ad,
  platform = MediumEnum.Desktop,
  mainModal,
  modals,
  onSuccess,
  onError,
  type = PaymentTypeEnum.ADVERTISE,
  paidServices,
  paidType,
  postpaidLimit,
  isBillingProfilePending = false
}: UsePaymentHandlerArgs) => {
  const [createPayment, { loading, error: gqlError }] = useMutation<
    CreatePaymentMutation,
    PaymentCreateInput
  >(CREATE_PAYMENT);

  const paymentCompletedRef = useRef(false);
  const paymentMethodRef = useRef<PaymentMethodEnum | undefined>(undefined);
  const isAddBalanceRef = useRef(false);
  const paymentWindowRef = useRef<Window | null>(null);
  const { openPaymentWindow } = usePaymentWindow();
  const popupWatchIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  const isBelowMobileL = useBelow('mobileL');

  const targetId = ad?.id;
  // eslint-disable-next-line no-underscore-dangle
  const typeName = ad?.__typename;
  const adTypeForMutation: AdTypeEnum | null = typeName
    ? mapTypenameToAdType(typeName)
    : null;

  const clearPopupInterval = () => {
    if (popupWatchIntervalRef.current) {
      clearInterval(popupWatchIntervalRef.current);
      popupWatchIntervalRef.current = null;
    }
  };

  const markPaymentAsFinished = () => {
    paymentCompletedRef.current = true;
  };

  const clearPaymentContext = () => {
    paymentMethodRef.current = undefined;
    isAddBalanceRef.current = false;
    paymentWindowRef.current = null;
  };

  const shouldCloseMainModal = (isAddBalance?: boolean) =>
    !(Boolean(isAddBalance) && type === PaymentTypeEnum.ADVERTISE);

  usePaymentMessageListener(
    () => {
      if (!paymentWindowRef.current) return;
      markPaymentAsFinished();
      clearPopupInterval();
      // paymentWindowRef.current.close();
      if (shouldCloseMainModal(isAddBalanceRef.current)) {
        mainModal.close();
      }
      onSuccess?.(paymentMethodRef.current, isAddBalanceRef.current);
      clearPaymentContext();
    },
    () => {
      if (!paymentWindowRef.current) return;
      clearPopupInterval();
      paymentWindowRef.current.close();
      mainModal.close();
      modals.error.open();
      onError?.();
      clearPaymentContext();
    }
  );

  const executePayment = async ({
    paidServiceId,
    targetId: execTargetId,
    platform: execPlatform,
    paymentMethodId,
    amount,
    isAddBalance,
    yigimTemplateGroup
  }: HandlePaymentArgs): Promise<PaymentResult> => {
    if (!paidServiceId) {
      return createFailedResult(
        'INVALID_INPUT',
        'Missing required payment input fields.'
      );
    }

    let computedVariables: PaymentCreateInput = {
      paidServiceId,
      paymentMethodId,
      platform: execPlatform,
      amount: amount ?? null,
      yigimTemplateGroup
    };

    if (!isAddBalance) {
      computedVariables = {
        ...computedVariables,
        targetId: execTargetId,
        targetType: adTypeForMutation
      };
    }

    try {
      const { data } = await createPayment({
        variables: computedVariables
      });
      const result = data?.createPayment?.paymentResult;

      if (!result) {
        return createFailedResult(
          'NO_RESULT',
          'No payment result returned from server.'
        );
      }

      return { ...result, errors: result.errors ?? [] };
    } catch (err) {
      console.error('Payment error:', err);
      return createFailedResult(
        'NETWORK_ERROR',
        err instanceof Error ? err.message : String(err)
      );
    }
  };

  const handleConfirmPayment = useCallback(
    async (
      method: PaymentMethodEnum,
      paidServiceOptionId: ExtraContentModalProps['activePaidServiceId'],
      isAppleGooglePayEnabled?: boolean,
      isAddBalance?: boolean
    ): Promise<PaymentResult | null> => {
      if (!paidServiceOptionId || (isAddBalance ? false : !targetId))
        return null;
      paymentMethodRef.current = method;
      isAddBalanceRef.current = Boolean(isAddBalance);

      let optionPrice: number | null;
      if (typeof paidServiceOptionId === 'string')
        optionPrice = selectOptionPrice(paidServices, paidServiceOptionId);
      else {
        optionPrice = paidServiceOptionId.price;
      }

      if (isWalletMethod(method)) {
        if (isBillingProfilePending) return null;

        const walletBalance = selectWalletBalance(paidServices);
        if (
          typeof optionPrice === 'number' &&
          !canPayWithWallet(walletBalance, optionPrice, paidType, postpaidLimit)
        ) {
          modals.emptyBalance.open();
          return null;
        }
      }

      // Open a blank window immediately to avoid popup blockers
      const paymentWindow = openPaymentWindow();
      try {
        const computedPaidServiceId: string =
          !!paidServiceOptionId && typeof paidServiceOptionId !== 'string'
            ? paidServiceOptionId.serviceId
            : paidServiceOptionId;

        const result = await executePayment({
          paidServiceId: computedPaidServiceId,
          targetId,
          paymentMethodId: method,
          platform,
          isAddBalance,
          amount: optionPrice,
          yigimTemplateGroup:
            isBelowMobileL && isAppleGooglePayEnabled
              ? PaymentCreateYigimTemplateGroupEnum.Gpl
              : PaymentCreateYigimTemplateGroupEnum.Tpl
        });

        switch (result.status) {
          case PaymentCreateStatusEnum.Success: {
            if (shouldCloseMainModal(isAddBalance)) {
              mainModal.close();
            }
            onSuccess?.(method, Boolean(isAddBalance));
            paymentWindow?.close();
            clearPaymentContext();
            break;
          }

          case PaymentCreateStatusEnum.Redirect: {
            if (result.redirectUrl && paymentWindow) {
              paymentWindowRef.current = paymentWindow;
              paymentWindow.location.href = result.redirectUrl;
              if (!isBelowMobileL) {
                clearPopupInterval();
                popupWatchIntervalRef.current = setInterval(() => {
                  if (!paymentWindow || paymentWindow.closed) {
                    clearPopupInterval();
                  }
                }, 500);
              }
            }
            break;
          }

          case PaymentCreateStatusEnum.Failure:
          default: {
            const emptyBalance =
              Array.isArray(result.errors) &&
              result.errors.some(
                error =>
                  (typeof error.code === 'string' &&
                    (EMPTY_BALANCE_CODE as readonly string[]).includes(
                      error.code
                    )) ||
                  (typeof error.message === 'string' &&
                    /no | balance/i.test(error.message))
              );

            mainModal.close();
            if (emptyBalance) {
              modals.emptyBalance.open();
            } else {
              const computedModal = isAddBalance
                ? modals.errorTopUp
                : modals.error;
              computedModal.open();
            }
            onError?.();
            clearPaymentContext();
            break;
          }
        }

        return result;
      } catch {
        mainModal.close();
        clearPaymentContext();
        return null;
      }
    },
    [
      targetId,
      adTypeForMutation,
      platform,
      executePayment,
      mainModal,
      modals,
      onError,
      onSuccess,
      paidServices,
      paidType,
      postpaidLimit,
      isBillingProfilePending
    ]
  );

  const handleRetryPayment = () => {
    if (type === PaymentTypeEnum.PUBLICATION) {
      modals.error.close();
      return;
    }
    mainModal.open();
    modals.error.close();
  };

  return {
    loading,
    gqlError,
    handleConfirmPayment,
    handleRetryPayment,
    markPaymentAsFinished,
    clearPopupInterval
  };
};
